import React, { useState } from 'react';
import applicationService from '../services/applicationService';

const TicketSection = ({ application, userRole, onRefresh }) => {
  const [ticketFile, setTicketFile] = useState(null);
  const [preDepartureFile, setPreDepartureFile] = useState(null);
  const [bookingDate, setBookingDate] = useState('');
  const [bookingNote, setBookingNote] = useState('');
  const [candidateReason, setCandidateReason] = useState('');
  const [uploading, setUploading] = useState(false);

  const canUploadTicket = ['HR', 'ADMIN', 'CHAIRMAN'].includes(userRole);
  const isCandidate = userRole === 'CANDIDATE';
  const ticket = application?.ticket || {};
  const bookingApproval = ticket?.bookingApproval || {};
  const bookingDecision = bookingApproval?.candidateDecision;
  const clearance = application?.clearance || {};
  const hasTicket = Boolean(ticket?.fileUrl);
  const hasPreDepartureCertificate = Boolean(ticket?.preDepartureCertificateUrl);
  const hasClearanceDocument = Boolean(clearance?.documentFileUrl);
  const hasBookingProposed = Boolean(bookingApproval?.proposedDate);
  const isBookingApproved = bookingDecision === 'approved';
  const isBookingPending = bookingDecision === 'pending';
  const isBookingRejected = bookingDecision === 'rejected';
  const canUploadTicketNow = canUploadTicket && application?.currentStage === 'TICKET' && hasClearanceDocument && isBookingApproved;

  const getDocumentViewUrl = (url, fileName = '') => {
    if (!url) return url;

    const lowerName = (fileName || '').toLowerCase();
    const lowerUrl = String(url).toLowerCase();
    const isPreviewableDoc =
      lowerName.endsWith('.pdf') ||
      lowerName.endsWith('.doc') ||
      lowerName.endsWith('.docx') ||
      lowerUrl.includes('.pdf') ||
      lowerUrl.includes('.doc') ||
      lowerUrl.includes('.docx');

    if (isPreviewableDoc) {
      return `https://docs.google.com/gview?embedded=1&url=${encodeURIComponent(url)}`;
    }

    return url;
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      alert('Only JPG, PNG, and PDF files are allowed');
      e.target.value = '';
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      e.target.value = '';
      return;
    }

    setTicketFile(file);
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!ticketFile) {
      alert('Please select a ticket document');
      return;
    }

    setUploading(true);
    try {
      const response = await applicationService.uploadTicket(application._id, ticketFile);
      if (response.success) {
        setTicketFile(null);
        const fileInput = document.getElementById('ticket-file-input');
        if (fileInput) fileInput.value = '';
        if (onRefresh) onRefresh();
        alert('Ticket uploaded successfully');
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to upload ticket');
    } finally {
      setUploading(false);
    }
  };

  const handleProposeBookingDate = async (e) => {
    e.preventDefault();

    if (!bookingDate) {
      alert('Please select booking date');
      return;
    }

    setUploading(true);
    try {
      const response = await applicationService.proposeTicketBookingDate(application._id, bookingDate, bookingNote);
      if (response.success) {
        setBookingDate('');
        setBookingNote('');
        if (onRefresh) onRefresh();
        alert('Ticket booking date sent to candidate for approval');
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to propose booking date');
    } finally {
      setUploading(false);
    }
  };

  const handleCandidateBookingDecision = async (action) => {
    if (action === 'reject' && !candidateReason.trim()) {
      alert('Please enter reason before rejecting');
      return;
    }

    setUploading(true);
    try {
      const response = await applicationService.respondTicketBookingDate(
        application._id,
        action,
        action === 'reject' ? candidateReason : ''
      );
      if (response.success) {
        setCandidateReason('');
        if (onRefresh) onRefresh();
        alert(action === 'approve' ? 'Booking date approved' : 'Booking date rejected');
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to submit booking response');
    } finally {
      setUploading(false);
    }
  };

  const handlePreDepartureFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      alert('Only JPG, PNG, and PDF files are allowed');
      e.target.value = '';
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      e.target.value = '';
      return;
    }

    setPreDepartureFile(file);
  };

  const handlePreDepartureUpload = async (e) => {
    e.preventDefault();

    if (!preDepartureFile) {
      alert('Please select a pre-departure certificate');
      return;
    }

    setUploading(true);
    try {
      const response = await applicationService.uploadPreDepartureCertificate(application._id, preDepartureFile);
      if (response.success) {
        setPreDepartureFile(null);
        const fileInput = document.getElementById('pre-departure-file-input');
        if (fileInput) fileInput.value = '';
        if (onRefresh) onRefresh();
        alert('Pre-departure certificate uploaded successfully');
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to upload pre-departure certificate');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
      <h3 className="m-0 mb-3 text-base font-semibold text-primary pb-1 border-b border-accent-green">Ticket Section</h3>

      {/* Reschedule Notification */}
      {ticket?.rescheduledFileUrl && (
        <div className="bg-orange-50 border border-orange-200 rounded-md p-3 mb-3">
          <div className="flex items-center gap-2 mb-2">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-orange-600">
              <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            <p className="m-0 text-sm font-semibold text-orange-800">
              Ticket Rescheduled
            </p>
          </div>
          <p className="m-0 text-sm text-orange-700">
            Your ticket has been rescheduled. Please check the updated ticket details below.
          </p>
          {ticket.rescheduledAt && (
            <p className="m-0 mt-1 text-xs text-orange-600">
              Last updated: {new Date(ticket.rescheduledAt).toLocaleString()}
            </p>
          )}
        </div>
      )}

      <div className="bg-[#f8f9fa] border border-[#dee2e6] rounded-md p-3 mb-3">
        <p className="m-0 mb-2 text-sm font-semibold text-text-primary">Ticket Booking Date Approval</p>
        {!hasBookingProposed && (
          <p className="m-0 text-sm text-text-secondary">No booking date proposed yet.</p>
        )}
        {hasBookingProposed && (
          <>
            <p className="m-0 text-sm text-text-primary">
              Proposed date: <strong>{new Date(bookingApproval.proposedDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}</strong>
            </p>
            <p className="m-0 mt-1 text-sm text-text-primary">
              Candidate response: <strong className={isBookingApproved ? 'text-green-700' : isBookingRejected ? 'text-red-700' : 'text-amber-700'}>{bookingDecision || 'pending'}</strong>
            </p>
            {bookingApproval?.candidateDecisionReason && (
              <p className="m-0 mt-1 text-xs text-text-secondary">Note/Reason: {bookingApproval.candidateDecisionReason}</p>
            )}
          </>
        )}
      </div>

      {canUploadTicket && application?.currentStage === 'TICKET' && (
        <form onSubmit={handleProposeBookingDate} className="bg-[#f8f9fa] border border-[#dee2e6] rounded-md p-3 mb-3">
          <div className="flex flex-col gap-3 md:gap-2">
            <label htmlFor="ticket-booking-date" className="font-medium text-[#495057] text-[0.9rem]">
              Set Ticket Booking Date
            </label>
            <input
              id="ticket-booking-date"
              type="date"
              value={bookingDate}
              onChange={(e) => setBookingDate(e.target.value)}
              disabled={uploading}
              className="p-2.5 border border-[#ced4da] rounded text-[0.95rem] bg-white"
            />
            <label htmlFor="ticket-booking-note" className="font-medium text-[#495057] text-[0.9rem]">
              Note (optional)
            </label>
            <textarea
              id="ticket-booking-note"
              value={bookingNote}
              onChange={(e) => setBookingNote(e.target.value)}
              disabled={uploading}
              rows={2}
              placeholder="Add note for candidate"
              className="p-2.5 border border-[#ced4da] rounded text-[0.95rem] bg-white"
            />
            <button
              type="submit"
              className="py-1.5 px-3 rounded-md font-semibold text-[13px] leading-8 h-fit min-h-0 cursor-pointer transition-all duration-200 border-none whitespace-nowrap inline-flex items-center justify-center align-middle box-border bg-accent-green text-white hover:bg-[#5a6b4d] disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={uploading || !bookingDate}
            >
              {uploading ? 'Saving...' : hasBookingProposed ? 'Update Booking Date Request' : 'Send Booking Date for Approval'}
            </button>
          </div>
        </form>
      )}

      {isCandidate && application?.currentStage === 'TICKET' && hasBookingProposed && isBookingPending && (
        <div className="bg-[#fff3cd] border border-[#ffeeba] rounded-md p-3 mb-3">
          <p className="m-0 mb-2 text-sm text-[#856404]">
            HR/Admin proposed <strong>{new Date(bookingApproval.proposedDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}</strong> for your ticket booking. Please approve or reject first.
          </p>
          <textarea
            value={candidateReason}
            onChange={(e) => setCandidateReason(e.target.value)}
            placeholder="Reason (required only if rejecting)"
            rows={2}
            disabled={uploading}
            className="w-full p-2.5 border border-[#ced4da] rounded text-[0.95rem] bg-white mb-2"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handleCandidateBookingDecision('approve')}
              disabled={uploading}
              className="py-1.5 px-3 rounded-md font-semibold text-[13px] border-none bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Approve Date
            </button>
            <button
              type="button"
              onClick={() => handleCandidateBookingDecision('reject')}
              disabled={uploading}
              className="py-1.5 px-3 rounded-md font-semibold text-[13px] border-none bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reject Date
            </button>
          </div>
        </div>
      )}

      {hasTicket ? (
        <div className="bg-[#f8f9fa] border border-[#dee2e6] rounded-md p-3 mb-3">
          <p className="m-0 mb-2 text-sm text-text-primary">
            Ticket document is uploaded.
          </p>
          <a
            href={getDocumentViewUrl(ticket.fileUrl, ticket.fileName)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 py-1.5 px-3 bg-white text-accent-green no-underline border border-accent-green rounded text-xs font-semibold transition-all duration-200 hover:bg-accent-green hover:text-white"
          >
            View Original Ticket
          </a>
          {ticket.uploadedAt && (
            <p className="m-0 mt-2 text-xs text-text-secondary">
              Uploaded on: {new Date(ticket.uploadedAt).toLocaleString()}
            </p>
          )}

          {/* Reschedule Ticket Section */}
          {ticket.rescheduledFileUrl && (
            <div className="mt-3 pt-3 border-t border-[#dee2e6]">
              <div className="flex items-center gap-2 mb-2">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-orange-600">
                  <path d="M12 2v4" />
                  <path d="m16.2 7.8 2.9-2.9" />
                  <path d="M18 12h4" />
                  <path d="m16.2 16.2 2.9 2.9" />
                  <path d="M12 18v4" />
                  <path d="m4.9 19.1 2.9-2.9" />
                  <path d="M2 12h4" />
                  <path d="m4.9 4.9 2.9 2.9" />
                </svg>
                <p className="m-0 text-sm font-semibold text-orange-600">
                  Rescheduled Ticket
                </p>
              </div>
              <p className="m-0 mb-2 text-sm text-text-primary">
                A new ticket has been uploaded due to rescheduling.
              </p>
              <div className="flex flex-wrap gap-2 mb-2">
                <a
                  href={getDocumentViewUrl(ticket.rescheduledFileUrl, ticket.rescheduledFileName)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 py-1.5 px-3 bg-white text-orange-600 no-underline border border-orange-600 rounded text-xs font-semibold transition-all duration-200 hover:bg-orange-600 hover:text-white"
                >
                  View Rescheduled Ticket
                </a>
              </div>
              {ticket.rescheduledRemark && (
                <div className="bg-orange-50 border border-orange-200 rounded p-2 mb-2">
                  <p className="m-0 text-xs font-medium text-orange-800 mb-1">Reschedule Reason:</p>
                  <p className="m-0 text-xs text-orange-700">{ticket.rescheduledRemark}</p>
                </div>
              )}
              {ticket.rescheduledAt && (
                <p className="m-0 text-xs text-text-secondary">
                  Rescheduled on: {new Date(ticket.rescheduledAt).toLocaleString()}
                </p>
              )}
            </div>
          )}

          {/* Reschedule History */}
          {ticket.rescheduleHistory && ticket.rescheduleHistory.length > 0 && (
            <div className="mt-3 pt-3 border-t border-[#dee2e6]">
              <div className="flex items-center gap-2 mb-2">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-600">
                  <path d="M3 3v5h5" />
                  <path d="m3 8 9-5 9 5v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                </svg>
                <p className="m-0 text-sm font-semibold text-gray-600">
                  Reschedule History ({ticket.rescheduleHistory.length})
                </p>
              </div>
              <div className="space-y-2">
                {ticket.rescheduleHistory.map((entry, index) => (
                  <div key={index} className="bg-gray-50 border border-gray-200 rounded p-2">
                    <div className="flex justify-between items-start mb-1">
                      <p className="m-0 text-xs font-medium text-gray-800">
                        Reschedule #{ticket.rescheduleHistory.length - index}
                      </p>
                      <p className="m-0 text-xs text-gray-600">
                        {new Date(entry.rescheduledAt).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </p>
                    </div>
                    {entry.remark && (
                      <p className="m-0 text-xs text-gray-700 mb-1">{entry.remark}</p>
                    )}
                    {entry.fileUrl && (
                      <a
                        href={getDocumentViewUrl(entry.fileUrl, entry.fileName)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 py-1 px-2 bg-white text-gray-600 no-underline border border-gray-300 rounded text-xs transition-all duration-200 hover:bg-gray-100"
                      >
                        View File
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-[#fff3cd] text-[#856404] p-3 rounded border border-[#ffeeba] mb-3 text-sm">
          Ticket has not been uploaded yet.
        </div>
      )}

      {canUploadTicketNow && (
        <form onSubmit={handleUpload} className="bg-[#f8f9fa] border border-[#dee2e6] rounded-md p-3">
          <div className="flex flex-col gap-3 md:gap-2">
            <label htmlFor="ticket-file-input" className="font-medium text-[#495057] text-[0.9rem]">
              Upload Ticket File
            </label>
            <input
              id="ticket-file-input"
              type="file"
              onChange={handleFileChange}
              accept=".jpg,.jpeg,.png,.pdf"
              disabled={uploading}
              className="p-2.5 border border-[#ced4da] rounded text-[0.95rem] bg-white"
            />
            <button
              type="submit"
              className="py-1.5 px-3 rounded-md font-semibold text-[13px] leading-8 h-fit min-h-0 cursor-pointer transition-all duration-200 border-none whitespace-nowrap inline-flex items-center justify-center align-middle box-border bg-accent-green text-white hover:bg-[#5a6b4d] disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={uploading || !ticketFile}
            >
              {uploading ? 'Uploading...' : hasTicket ? 'Replace Ticket' : 'Upload Ticket'}
            </button>
            <small className="text-[#6c757d] text-[0.85rem]">Max 5MB. Allowed: JPG, PNG, PDF</small>
          </div>
        </form>
      )}

      {canUploadTicket && application?.currentStage === 'TICKET' && !canUploadTicketNow && (
        <div className="bg-[#e7f3ff] text-[#004085] p-3 rounded border border-[#b8daff] mb-3 text-[0.9rem]">
          Ticket upload unlocks only after clearance document is uploaded and candidate approves the proposed booking date.
        </div>
      )}

      {hasPreDepartureCertificate ? (
        <div className="bg-[#f8f9fa] border border-[#dee2e6] rounded-md p-3 mb-3 mt-3">
          <p className="m-0 mb-2 text-sm text-text-primary">
            Pre-departure certificate is uploaded.
          </p>
          <a
            href={getDocumentViewUrl(ticket.preDepartureCertificateUrl, ticket.preDepartureCertificateFileName)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 py-1.5 px-3 bg-white text-accent-green no-underline border border-accent-green rounded text-xs font-semibold transition-all duration-200 hover:bg-accent-green hover:text-white"
          >
            View Pre-Departure Certificate
          </a>
          {ticket.preDepartureCertificateUploadedAt && (
            <p className="m-0 mt-2 text-xs text-text-secondary">
              Uploaded on: {new Date(ticket.preDepartureCertificateUploadedAt).toLocaleString()}
            </p>
          )}
        </div>
      ) : (
        <div className="bg-[#e7f3ff] text-[#004085] p-3 rounded border border-[#b8daff] mb-3 text-sm mt-3">
          Pre-departure certificate is optional. You can upload it now or later if needed.
        </div>
      )}

      {canUploadTicket && application?.currentStage === 'TICKET' && (
        <form onSubmit={handlePreDepartureUpload} className="bg-[#f8f9fa] border border-[#dee2e6] rounded-md p-3">
          <div className="flex flex-col gap-3 md:gap-2">
            <label htmlFor="pre-departure-file-input" className="font-medium text-[#495057] text-[0.9rem]">
              Upload Pre-Departure Certificate
            </label>
            <input
              id="pre-departure-file-input"
              type="file"
              onChange={handlePreDepartureFileChange}
              accept=".jpg,.jpeg,.png,.pdf"
              disabled={uploading}
              className="p-2.5 border border-[#ced4da] rounded text-[0.95rem] bg-white"
            />
            <button
              type="submit"
              className="py-1.5 px-3 rounded-md font-semibold text-[13px] leading-8 h-fit min-h-0 cursor-pointer transition-all duration-200 border-none whitespace-nowrap inline-flex items-center justify-center align-middle box-border bg-accent-green text-white hover:bg-[#5a6b4d] disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={uploading || !preDepartureFile}
            >
              {uploading ? 'Uploading...' : hasPreDepartureCertificate ? 'Replace Pre-Departure Certificate' : 'Upload Pre-Departure Certificate'}
            </button>
            <small className="text-[#6c757d] text-[0.85rem]">Max 5MB. Allowed: JPG, PNG, PDF</small>
          </div>
        </form>
      )}

      {isCandidate && (
        <div className="bg-[#e7f3ff] text-[#004085] p-3 rounded border border-[#b8daff] text-[0.9rem]">
          Ticket and pre-departure certificate can be uploaded only by HR/Admin. You can view/download files, and approve/reject ticket booking date.
        </div>
      )}
    </div>
  );
};

export default TicketSection;

